import { useCallback, useEffect, useRef, useState } from 'react';

import { captureFunnelEvent } from '../../services/telemetry';

export type FeedbackTrigger = 'download' | 'edit-commit' | 'dwell' | 'manual';
export type FeedbackPageType = 'pdf' | 'xml';

/** Props shared by the project toolbar and mobile action bar. */
export interface ProjectFeedbackProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted: () => void;
  trigger: FeedbackTrigger;
  pageType: FeedbackPageType;
  onManualOpen?: () => void;
}

const DISMISSED_KEY_PREFIX = 'projectFeedbackDismissed:';
const DWELL_MS = 60_000;
const DWELL_IDLE_MS = 3_000;
const VALUE_MOMENT_IDLE_MS = 800;
const DOWNLOAD_DELAY_MS = 1_500;
const GATE_POLL_MS = 250;

const dismissedKey = (projectId: string) =>
  `${DISMISSED_KEY_PREFIX}${projectId}`;

const readDismissed = (projectId: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(dismissedKey(projectId)) === '1';
  } catch {
    return false;
  }
};

/**
 * Opens the project-page feedback popover after a value moment,
 * once idle and not blocked (tutorial, edit, or already answered).
 */
export function useProjectFeedbackPrompt({
  ready,
  enabled,
  projectId,
  pageType,
  hasAnsweredImprovementSurvey,
  isEditMode,
  blocked = false,
}: {
  ready: boolean;
  enabled: boolean;
  projectId?: string;
  pageType: FeedbackPageType;
  hasAnsweredImprovementSurvey: boolean;
  isEditMode: boolean;
  blocked?: boolean;
}) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [answeredThisSession, setAnsweredThisSession] = useState(false);
  const [dismissedThisProject, setDismissedThisProject] = useState(() =>
    projectId ? readDismissed(projectId) : false
  );
  const [armedTrigger, setArmedTrigger] = useState<FeedbackTrigger | null>(
    null
  );
  const [activeTrigger, setActiveTrigger] = useState<FeedbackTrigger>('manual');
  const hasAutoOpened = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const armedAtRef = useRef(0);
  const readyAtRef = useRef<number | null>(null);
  const closedAfterSubmitRef = useRef(false);
  const feedbackOpenRef = useRef(false);
  feedbackOpenRef.current = feedbackOpen;

  useEffect(() => {
    setDismissedThisProject(projectId ? readDismissed(projectId) : false);
    setAnsweredThisSession(false);
    setArmedTrigger(null);
    setFeedbackOpen(false);
    setActiveTrigger('manual');
    hasAutoOpened.current = false;
    armedAtRef.current = 0;
    closedAfterSubmitRef.current = false;
  }, [projectId]);

  useEffect(() => {
    if (ready) {
      readyAtRef.current = Date.now();
    } else {
      readyAtRef.current = null;
    }
  }, [ready, projectId]);

  const canPrompt =
    enabled &&
    ready &&
    Boolean(projectId) &&
    !hasAnsweredImprovementSurvey &&
    !answeredThisSession &&
    !dismissedThisProject;

  const armTrigger = useCallback(
    (trigger: Exclude<FeedbackTrigger, 'manual'>) => {
      if (hasAutoOpened.current || !canPrompt) return;
      setArmedTrigger((prev) => {
        if (prev) return prev;
        armedAtRef.current = Date.now();
        return trigger;
      });
    },
    [canPrompt]
  );

  // Restarted when the tutorial unblocks so a just-onboarded user
  // is not asked seconds later.
  useEffect(() => {
    if (!canPrompt || blocked || isEditMode) return;
    const timer = setTimeout(() => armTrigger('dwell'), DWELL_MS);
    return () => clearTimeout(timer);
  }, [canPrompt, blocked, isEditMode, armTrigger]);

  useEffect(() => {
    if (!canPrompt) return;
    lastActivityRef.current = Date.now();
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    document.addEventListener('pointerdown', markActive);
    document.addEventListener('keydown', markActive);
    document.addEventListener('wheel', markActive, { passive: true });
    document.addEventListener('scroll', markActive, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', markActive);
      document.removeEventListener('keydown', markActive);
      document.removeEventListener('wheel', markActive);
      document.removeEventListener('scroll', markActive);
    };
  }, [canPrompt]);

  useEffect(() => {
    if ((isEditMode || blocked) && feedbackOpen) {
      setFeedbackOpen(false);
    }
  }, [isEditMode, blocked, feedbackOpen]);

  useEffect(() => {
    if (hasAutoOpened.current || !armedTrigger || feedbackOpen || !canPrompt) {
      return;
    }

    const tryOpen = () => {
      if (hasAutoOpened.current) return;
      if (isEditMode || blocked) return;
      const idleMs =
        armedTrigger === 'dwell' ? DWELL_IDLE_MS : VALUE_MOMENT_IDLE_MS;
      if (Date.now() - lastActivityRef.current < idleMs) return;
      if (
        armedTrigger === 'download' &&
        Date.now() - armedAtRef.current < DOWNLOAD_DELAY_MS
      ) {
        return;
      }

      hasAutoOpened.current = true;
      setActiveTrigger(armedTrigger);
      setFeedbackOpen(true);
      const secondsSinceReady = readyAtRef.current
        ? Math.round((Date.now() - readyAtRef.current) / 1000)
        : null;
      captureFunnelEvent('feedback_prompt_shown', {
        trigger: armedTrigger,
        projectId,
        type: pageType,
        secondsSinceReady,
      });
    };

    const interval = setInterval(tryOpen, GATE_POLL_MS);
    tryOpen();
    return () => clearInterval(interval);
  }, [
    armedTrigger,
    feedbackOpen,
    canPrompt,
    isEditMode,
    blocked,
    projectId,
    pageType,
  ]);

  const markFeedbackSubmitted = useCallback(() => {
    closedAfterSubmitRef.current = true;
    setAnsweredThisSession(true);
  }, []);

  const onFeedbackOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        closedAfterSubmitRef.current = false;
        setActiveTrigger('manual');
        setFeedbackOpen(true);
        const secondsSinceReady = readyAtRef.current
          ? Math.round((Date.now() - readyAtRef.current) / 1000)
          : null;
        captureFunnelEvent('feedback_prompt_shown', {
          trigger: 'manual',
          projectId,
          type: pageType,
          secondsSinceReady,
        });
        return;
      }
      if (feedbackOpenRef.current && !closedAfterSubmitRef.current) {
        captureFunnelEvent('feedback_prompt_dismissed', {
          trigger: activeTrigger,
          projectId,
          type: pageType,
        });
      }
      setFeedbackOpen(false);
    },
    [projectId, pageType, activeTrigger]
  );

  return {
    feedbackOpen,
    onFeedbackOpenChange,
    markFeedbackSubmitted,
    armTrigger,
    activeTrigger,
  };
}
